// services/userService.ts
import { apiService } from './api';

export interface User {
  users_id: number;
  users_name: string;
  email: string;
  phone?: string;
  designation: string;
  role: {
    id: number;
    role_name: string;
  };
  role_id: number;
  is_active: boolean;
  is_superuser?: boolean;
  last_login?: string;
  created_at: string;
  created_by: string;
  updated_at?: string;
  updated_by?: string;
}

export interface Role {
  id: number;
  role_name: string;
}

export interface CreateUserRequest {
  users_name: string;
  email: string;
  phone?: string;
  designation: string;
  password: string;
  confirmPassword: string; // frontend only — stripped before API call
  role_id: number;
  is_active?: boolean;
  is_superuser?: boolean;
}

export interface UpdateUserRequest {
  users_name?: string;
  role_id?: number;
  designation: string;
  is_active?: boolean;
  is_superuser?: boolean;
  password?: string;
}

export interface UserListParams {
  page?: number;
  limit?: number;
  search?: string;
  role_id?: number;
  is_active?: boolean;
}

export interface UserListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: User[];
}

class UserService {
  /**
   * Get all users (paginated)
   */
  async getUsers(params?: UserListParams): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.role_id) queryParams.append('role_id', params.role_id.toString());
    if (params?.is_active !== undefined)
      queryParams.append('is_active', params.is_active.toString());

    const queryString = queryParams.toString();
    const url = `/auth/users/${queryString ? `?${queryString}` : ''}`;

    return apiService.get<UserListResponse>(url);
  }

  /**
   * Get single user by ID
   */
  async getUserById(userId: number): Promise<User> {
    return apiService.get<User>(`/auth/users/${userId}/`);
  }

  /**
   * Create new user.
   * `confirmPassword` is a frontend-only field and is removed before the API call.
   */
  async createUser(data: CreateUserRequest): Promise<User> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { confirmPassword, ...payload } = data;
    return apiService.post<User>('/auth/users/', payload);
  }

  /**
   * Update existing user (PATCH — partial update)
   */
  async updateUser(userId: number, data: UpdateUserRequest): Promise<User> {
    // Strip empty password so backend doesn't try to hash an empty string
    const payload: Partial<UpdateUserRequest> = { ...data };
    if (!payload.password || payload.password.trim() === '') {
      delete payload.password;
    }
    return apiService.patch<User>(`/auth/users/${userId}/`, payload);
  }

  /**
   * Delete user
   */
  async deleteUser(userId: number): Promise<void> {
    return apiService.delete(`/auth/users/${userId}/`);
  }

  /**
   * Get all available roles
   */
  async getRoles(): Promise<Role[]> {
    const response = await apiService.get<any>('/auth/roles/');

    if (Array.isArray(response)) {
      return response;
    } else if (response && Array.isArray(response.results)) {
      return response.results;
    }

    console.error('Unexpected roles response format:', response);
    return [];
  }

  /**
   * Toggle user active status
   */
  async toggleUserStatus(userId: number, isActive: boolean): Promise<User> {
    return apiService.patch<User>(`/auth/users/${userId}/`, { is_active: isActive });
  }

  /**
   * Reset user password (admin action)
   */
  async resetUserPassword(
    userId: number,
    newPassword: string,
  ): Promise<{ success: boolean; message: string }> {
    return apiService.post(`/auth/users/${userId}/reset-password/`, {
      password: newPassword,
    });
  }

  /**
   * Bulk update users
   */
  async bulkUpdateUsers(
    userIds: number[],
    data: UpdateUserRequest,
  ): Promise<{ success: boolean; message: string }> {
    return apiService.post('/auth/users/bulk-update/', { user_ids: userIds, data });
  }

    /**
   * Download every core table as one .xlsx workbook (DATA_MANAGER only).
   * Filename: download_all_data_ddMonYYYY_HHMMSS.xlsx
   */
    async downloadAllData(): Promise<void> {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const stamp =
        `${pad(d.getDate())}${months[d.getMonth()]}${d.getFullYear()}_` +
        `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      return apiService.download('/trades/download-all-data/', `download_all_data_${stamp}.xlsx`);
    }
}

export const userService = new UserService();